import { Collapse, Spin } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import {
  CIMSCollapsePanelOptions,
  CIMSCollapseProps,
  CIMSPanelCount,
} from './types'
import CIMSCollapseCountsExtra from './components/CIMSCollapseCountsExtra'
import CIMSCollapseRecursiveCountExtra from './components/CIMSCollapseRecursiveCountExtra'
import { countReducer } from './utils'
import CIMSCollapseCountExtra from './components/CIMSCollapseCountExtra'
import './index.css'

/**
 * @see {@link CIMSCollapseProps}
 */
function CIMSCollapse({
  panels,
  onCountChange,
  noPanelsContent,
  loading,
  multiple,
  overflowX,
  ...rest
}: CIMSCollapseProps) {
  const [counts, setCounts] = useState<CIMSPanelCount['counts']>([])
  const [count, setCount] = useState<CIMSPanelCount['count']>(0)
  useEffect(() => {
    onCountChange?.({ count, counts })
    // don't include onCountChange to avoid infinite re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counts, count])
  const change = useCallback<
    (
      options: { count: number | undefined } & Pick<
        CIMSCollapsePanelOptions,
        'key'
      >
    ) => void
  >(
    ({ count: changed, key }) =>
      setCounts((prev) => {
        const counts = [
          ...prev.filter((c) => key !== c.key),
          { key, count: changed ?? 0 },
        ]
        setCount(counts.reduce(countReducer, 0))
        return counts
      }),
    []
  )
  let children
  if (!panels?.length) {
    if (!noPanelsContent) children = null
    children = <>{noPanelsContent}</>
  } else
    children = (
      <Collapse accordion={!multiple} {...rest}>
        {(panels ?? []).map(
          ({ children, extra: extraProps, header: headerProps, ...rest }) => {
            let extra
            if (extraProps) {
              if (extraProps.type === 'counts')
                extra = (
                  <CIMSCollapseCountsExtra
                    {...extraProps}
                    onCountsChange={(counts) => {
                      setCounts((prev) => {
                        const newCounts = [
                          ...prev.filter(({ key }) => rest.key !== key),
                          {
                            key: rest.key,
                            count: counts.reduce(countReducer, 0),
                          },
                        ]
                        setCount(newCounts.reduce(countReducer, 0))
                        return newCounts
                      })
                      extraProps.onCountsChange?.(counts)
                    }}
                  />
                )
              else if (extraProps.type === 'count')
                extra = (
                  <CIMSCollapseCountExtra
                    {...extraProps}
                    onCountChange={(count) => {
                      change({ ...rest, count })
                      extraProps.onCountChange?.(count)
                    }}
                  />
                )
              else if (extraProps.type === 'recursive')
                extra = (
                  <CIMSCollapseRecursiveCountExtra
                    onCountChange={(count) => {
                      change({ ...rest, count })
                      extraProps.onCountChange?.(count)
                    }}
                    {...extraProps}
                  />
                )
              else extra = extraProps.children
            } else extra = null
            let header
            if (typeof headerProps === 'string') header = headerProps
            else if (headerProps.type === 'described')
              header = !!headerProps.description
                ? `${headerProps.name} : ${headerProps.description}`
                : headerProps.name
            else header = headerProps.children
            /**
             * we've overwritten some of the header styles in index.css
             */
            return (
              <Collapse.Panel header={header} {...rest} extra={extra}>
                {overflowX ? (
                  <div style={{ overflowX: 'auto' }}>{children}</div>
                ) : (
                  children
                )}
              </Collapse.Panel>
            )
          }
        )}
      </Collapse>
    )
  return <Spin spinning={loading ?? false}>{children}</Spin>
}

export default CIMSCollapse
